import { IconArrowLeft, IconPencil, IconPrinter } from '@tabler/icons-react';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Banner, Spinner, useToast } from '@/components/common';
import { useResource } from '@/hooks';
import { errorMessage } from '@/lib/api-client';
import { navigate } from '@/lib/router';
import type { Offer } from '@/types/api';
import { useAuth } from '../auth';
import { useDoctor } from '../doctors';
import { useClinicSettings, useLogoUrl } from '../settings';
import { offersApi } from './api';
import { draftToInput, EMPTY_DRAFT, type OfferDraft, OfferEditor } from './components/OfferEditor';
import { OfferPaper } from './components/OfferPaper';

const toDraft = (o: Offer): OfferDraft => ({
  patientId: o.patient?.id ?? null,
  patientName: o.patientName,
  linkedName: o.patient?.name ?? null,
  steps: o.steps,
  total: String(o.totalRsd),
  note: o.note,
});

function Header({ title }: { title: string }) {
  const { t } = useTranslation();
  return (
    <div className="page-head no-print">
      <button
        type="button"
        className="icon-btn"
        onClick={() => navigate({ name: 'offers' })}
        aria-label={t('offers.back')}
      >
        <IconArrowLeft size={24} />
      </button>
      <div className="page-head__title">
        <h1>{title}</h1>
      </div>
    </div>
  );
}

/** A new offer (`id` 'new'), or an issued one: shown as printed, reprinted or corrected. */
export function OfferPage({ id }: { id: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const { doctorId } = useDoctor();
  const clinic = useClinicSettings(user?.clinic?.id ?? '');
  const logoUrl = useLogoUrl(clinic?.logoVersion ?? null);
  const isNew = id === 'new';
  const offer = useResource<Offer | null>(
    () => (isNew ? Promise.resolve(null) : offersApi.get(id)),
    id
  );
  const [draft, setDraft] = useState<OfferDraft | null>(isNew ? EMPTY_DRAFT : null);
  const [busy, setBusy] = useState(false);
  const editDraft: Dispatch<SetStateAction<OfferDraft>> = (update) =>
    setDraft((d) => d && (typeof update === 'function' ? update(d) : update));

  const save = async () => {
    const input = draft && draftToInput(draft);
    if (!input) return;
    setBusy(true);
    try {
      if (isNew) {
        const created = await offersApi.create({ ...input, dentistId: doctorId });
        navigate({ name: 'offer', id: created.id }, true);
      } else {
        offer.setData(await offersApi.update(id, input));
        setDraft(null);
      }
      toast.show(t('offers.saved'));
    } catch (err) {
      toast.show(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (offer.error) {
    return (
      <>
        <Header title={t('offers.title')} />
        <Banner tone="error">{errorMessage(offer.error)}</Banner>
      </>
    );
  }

  if (draft) {
    return (
      <div className="stack offer-page">
        <Header
          title={isNew ? t('offers.new') : t('offers.editTitle', { number: offer.data?.number })}
        />
        <OfferEditor
          draft={draft}
          onChange={editDraft}
          busy={busy}
          onSave={save}
          onCancel={isNew ? () => navigate({ name: 'offers' }) : () => setDraft(null)}
        />
      </div>
    );
  }

  if (!offer.data) return <Spinner />;
  const current = offer.data;
  return (
    <div className="stack offer-page">
      <Header title={t('offers.numberTitle', { number: current.number })} />
      <div className="row no-print">
        <button type="button" className="btn" onClick={() => setDraft(toDraft(current))}>
          <IconPencil size={18} /> {t('offers.edit')}
        </button>
        <button type="button" className="btn btn--dark" onClick={() => window.print()}>
          <IconPrinter size={18} /> {t('offers.print')}
        </button>
      </div>
      <div className="offer-preview">
        <OfferPaper offer={current} clinic={clinic} logoUrl={logoUrl} />
      </div>
      {/* The printed copy lives outside the app shell, so nothing else reaches the paper. */}
      {createPortal(
        <div className="print-root">
          <OfferPaper offer={current} clinic={clinic} logoUrl={logoUrl} />
        </div>,
        document.body
      )}
    </div>
  );
}
